// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { distanceMeters } from '@/services/geo';

import type { LatLon } from '@/features/hole/holeview/tileMath';

/**
 * Keeps a course's stored position honest against the geometry actually marked on it.
 *
 * The course coordinate is not decoration: it frames every hole that has nothing marked yet,
 * and it is the point the imagery analyser centres its tiles on. When it is wrong — an
 * import that resolved to the wrong place, or a course typed in by hand — every unmapped
 * hole opens onto the wrong ground, and no amount of correcting one hole fixes the next one.
 *
 * So marking a hole a long way from the stored position is treated as evidence that the
 * stored position is what is wrong. An admin who has just placed a green on a fairway they
 * can see is a better source than whatever coordinate the course was created with.
 */

/**
 * How far marked geometry may sit from the stored position before the position is the thing
 * assumed to be wrong.
 *
 * A golf course is roughly a kilometre or two across, so a tee genuinely can be a long way
 * from the centre. Two kilometres is past the far corner of a large course, which makes it
 * hard to reach by legitimately marking a hole and easy to reach when the stored coordinate
 * points at another town.
 */
export const POSITION_CORRECTION_THRESHOLD_M = 2000;

/** Great-circle distance in metres — the shared implementation in services/geo,
 *  kept under this module's historical export name. */
export const distanceMetres = distanceMeters;

/** The middle of everything mapped. Null when nothing is. */
export const centroidOf = (points: readonly LatLon[]): LatLon | null => {
  if (points.length === 0) return null;
  return {
    lat: points.reduce((sum, point) => sum + point.lat, 0) / points.length,
    lon: points.reduce((sum, point) => sum + point.lon, 0) / points.length,
  };
};

/**
 * The position a course should be moved to, or null to leave it alone.
 *
 * Returns a coordinate only when there is something to move to and a reason to move: either
 * the course has no position at all, or its position is implausibly far from the geometry
 * that has been marked on it. A course whose stored position is merely a little off is left
 * as it is — it is already good enough to frame holes with, and quietly dragging it around
 * after every edit would make the map restless for no gain.
 */
export const correctedCoursePosition = (
  stored: LatLon | null,
  markedPoints: readonly LatLon[]
): LatLon | null => {
  const centre = centroidOf(markedPoints);
  if (!centre) return null;
  if (!stored) return centre;
  return distanceMetres(stored, centre) > POSITION_CORRECTION_THRESHOLD_M ? centre : null;
};
